/**
 * @author Milton Filho
 */

const SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let cachedPromise: Promise<void> | null = null;

// Loads Google Identity Services (~73 KiB) on demand. Used to be a synchronous
// <script> tag in index.html, which blocked first paint on every page even
// though GIS is only needed on the login flow.
export const loadGoogleIdentityServices = (): Promise<void> => {
  if (cachedPromise) {
    return cachedPromise;
  }

  if (typeof window === 'undefined') {
    cachedPromise = Promise.resolve();
    return cachedPromise;
  }

  cachedPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Failed to load Google Identity Services')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.addEventListener(
      'load',
      () => {
        script.dataset.loaded = 'true';
        resolve();
      },
      { once: true },
    );
    script.addEventListener(
      'error',
      () => {
        cachedPromise = null;
        reject(new Error('Failed to load Google Identity Services'));
      },
      { once: true },
    );
    document.head.appendChild(script);
  });

  return cachedPromise;
};
